import type { CustomerGeneratedImage } from "@/types/order";

interface ImageActionsProps {
  images: CustomerGeneratedImage[];
  selectedIdxs: Set<number>;
  maxDownloadCount: number;
  onToggleSelectAll: () => void;
  onRegenerate: () => void;
  onDownloadSelected: () => void;
  onDownloadAll: () => void;
  isRegenerating: boolean;
  isDownloading: boolean;
  isExpired?: boolean;
}

export default function ImageActions({
  images,
  selectedIdxs,
  maxDownloadCount,
  onToggleSelectAll,
  onRegenerate,
  onDownloadSelected,
  onDownloadAll,
  isRegenerating,
  isDownloading,
  isExpired = false,
}: ImageActionsProps) {
  const selectedCount = selectedIdxs.size;
  const allSelected = selectedCount === images.length && images.length > 0;
  const totalImages = images.length;

  // 재생성 가능 여부: 선택된 이미지 중 canRegenerate=false인 것이 있거나 기간 만료 시 비활성
  const selectedImages = images.filter((img) =>
    selectedIdxs.has(img.generatedImageIdx),
  );
  const hasNonRegeneratable = selectedImages.some((img) => !img.canRegenerate);
  const canRegenerate =
    selectedCount > 0 && !hasNonRegeneratable && !isRegenerating && !isExpired;

  // 다운로드 가능 여부
  const canDownloadSelected =
    selectedCount > 0 &&
    selectedCount <= maxDownloadCount &&
    !isDownloading &&
    !isExpired;
  const canDownloadAll =
    totalImages > 0 &&
    totalImages <= maxDownloadCount &&
    !isDownloading &&
    !isExpired;
  const downloadOverLimit = selectedCount > maxDownloadCount;

  return (
    <div className="space-y-3">
      <div className="flex items-center flex-wrap gap-2">
        {/* 전체 선택 */}
        <button
          onClick={onToggleSelectAll}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            allSelected
              ? "bg-[#C9A96E] text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          <i
            className={
              allSelected ? "ri-checkbox-fill" : "ri-checkbox-blank-line"
            }
          ></i>
          전체 선택
        </button>

        {/* 선택 재생성 */}
        <button
          onClick={onRegenerate}
          disabled={!canRegenerate}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            canRegenerate
              ? "bg-violet-100 text-violet-700 hover:bg-violet-200 cursor-pointer"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {isRegenerating ? (
            <i className="ri-loader-4-line animate-spin"></i>
          ) : (
            <i className="ri-refresh-line"></i>
          )}
          {isRegenerating
            ? "재생성 중..."
            : `선택 재생성${selectedCount > 0 ? ` (${selectedCount}장)` : ""}`}
        </button>

        {/* 선택 다운로드 */}
        <button
          onClick={onDownloadSelected}
          disabled={!canDownloadSelected}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            canDownloadSelected
              ? "bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {isDownloading ? (
            <i className="ri-loader-4-line animate-spin"></i>
          ) : (
            <i className="ri-download-line"></i>
          )}
          선택 다운로드{selectedCount > 0 ? ` (${selectedCount}장)` : ""}
        </button>

        {/* 전체 다운로드 */}
        <button
          onClick={onDownloadAll}
          disabled={!canDownloadAll}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            canDownloadAll
              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          <i className="ri-download-2-line"></i>
          전체 다운로드 ({totalImages}장)
        </button>
      </div>

      {/* PC 권장 안내 */}
      <p className="text-xs text-gray-500 flex items-center gap-1">
        <i className="ri-computer-line"></i>
        다운로드 및 재생성은 PC 환경에서 진행하시는 것을 권장드립니다.
      </p>

      {/* 다운로드 한도 초과 경고 */}
      {downloadOverLimit && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          최대 {maxDownloadCount}장까지 다운로드 가능합니다.
        </p>
      )}

      {/* 재생성 불가 이미지 선택 시 경고 */}
      {hasNonRegeneratable && selectedCount > 0 && !isExpired && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          재생성 횟수를 모두 사용한 이미지가 포함되어 있습니다.
        </p>
      )}

      {/* 기간 만료 안내 */}
      {isExpired && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          서비스 제공기간(결제일로부터 3개월)이 만료되어 재생성·다운로드를 이용하실 수 없습니다.
        </p>
      )}
    </div>
  );
}
